/** Readiness, derived in the browser because SES-021's read does not exist yet. */

import type { EventBinding } from "@nebulize/sdk";

import type { StatusTone } from "@/components/ui";

import type { CanvasPairing } from "../../account/lib/queries";
import { canvasesOf, requirementsOf, triggersOf } from "../../lib/catalog";
import { isLive, runStatusOf } from "../../lib/runState";
import {
  countOf,
  interfaceProviders,
  interfaceWiring,
  legalTargetKinds,
  needsMachine,
  positionsOf,
  requirementKind,
  servedInstead,
} from "../../lib/requirements";
import type { SpaceActivities } from "../../spaces/lib/queries";
import type {
  AddedActivityRow,
  ConnectionRow,
  NamedCanvas,
  NamedRequirement,
  NamedTrigger,
} from "../../types";

/** `ReadinessState`, closed. It says what is true, never what to do. */
export type ReadinessState =
  | "ready"
  | "unwired"
  | "ambiguous"
  | "unavailable"
  | "optional_unwired";

/** Worst first. An activity's own state is the worst of its requirements'. */
const ORDER: readonly ReadinessState[] = [
  "unavailable",
  "ambiguous",
  "unwired",
  "optional_unwired",
  "ready",
];

export function rank(state: ReadinessState): number {
  return ORDER.indexOf(state);
}

/** Whether a state is one of the three that hold Start back. */
export function blocks(state: ReadinessState): boolean {
  return state === "unwired" || state === "ambiguous" || state === "unavailable";
}

/** One requirement of one added activity, as far as the client can tell. */
export interface RequirementReadiness {
  requirement: NamedRequirement;
  state: ReadinessState;
  filled: number;
  available: number;
  /** The one remedy, as a phrase naming what would fix it. */
  remedy: string;
}

export interface ActivityReadiness {
  row: AddedActivityRow;
  state: ReadinessState;
  requirements: RequirementReadiness[];
  /** True when no manifest was read, so nothing could be derived and Start stays enabled. */
  underived: boolean;
}

function machineAttached(connections: readonly ConnectionRow[]): boolean {
  return connections.some((one) => one.kind === "machine");
}

function readOne(
  added: AddedActivityRow,
  requirement: NamedRequirement,
  activities: SpaceActivities,
  connections: readonly ConnectionRow[],
): RequirementReadiness {
  const positions = positionsOf(added, requirement);
  const available = countOf(added, requirement);
  const optional = requirement.required === false;

  if (requirementKind(requirement.type) === "interface") {
    const providers = interfaceProviders(
      requirement.type,
      activities.rows,
      activities.entryFor,
      added.added_activity_id,
    );
    const readings = positions.map((one) =>
      interfaceWiring(one.binding, activities.rows, providers, activities.liveRead),
    );
    const filled = readings.filter(
      (one) => one.state !== "unwired" && one.state !== "provider_removed",
    ).length;
    if (filled >= available && available > 0) {
      return { requirement, state: "ready", filled, available, remedy: "" };
    }

    const owing = readings.find(
      (one) => one.state === "unwired" || one.state === "provider_removed",
    );
    if (owing?.state === "provider_removed") {
      return {
        requirement,
        state: optional ? "optional_unwired" : "unwired",
        filled,
        available,
        remedy: owing.remedy,
      };
    }
    if (providers.length === 0) {
      return {
        requirement,
        state: optional ? "optional_unwired" : "unavailable",
        filled,
        available,
        remedy: `add an activity providing ${requirement.type}`,
      };
    }
    if (providers.length > 1) {
      return {
        requirement,
        state: optional ? "optional_unwired" : "ambiguous",
        filled,
        available,
        remedy: `pick one of ${String(providers.length)} providers`,
      };
    }
    return {
      requirement,
      state: optional ? "optional_unwired" : "unwired",
      filled,
      available,
      remedy: `choose ${providers[0]?.name ?? ""} to serve it`,
    };
  }

  const filled = positions.filter((one) => one.binding !== null).length;
  if (filled >= available && available > 0) {
    return { requirement, state: "ready", filled, available, remedy: "" };
  }

  if (needsMachine(requirement.type) && !machineAttached(connections)) {
    return {
      requirement,
      state: optional ? "optional_unwired" : "unavailable",
      filled,
      available,
      remedy: "attach a machine to this space",
    };
  }

  const kinds = legalTargetKinds(requirement.type);
  return {
    requirement,
    state: optional ? "optional_unwired" : "unwired",
    filled,
    available,
    // A feed position is bound like any other now (RUN-038), so the only types left with no legal
    // kind are the ones something else serves. `servedInstead` is the one place that answers that.
    remedy:
      kinds.length === 0
        ? (servedInstead(requirement.type) ?? "")
        : `bind ${kinds.join(" or ")}`,
  };
}

/** Every requirement of one added activity, worst first. */
export function readinessOf(
  added: AddedActivityRow,
  activities: SpaceActivities,
  connections: readonly ConnectionRow[],
): ActivityReadiness {
  const entry = activities.entryFor(added);
  if (entry === undefined) {
    return { row: added, state: "ready", requirements: [], underived: true };
  }

  const requirements = requirementsOf(entry)
    .map((one) => readOne(added, one, activities, connections))
    .sort((left, right) => rank(left.state) - rank(right.state));

  const worst = requirements[0]?.state ?? "ready";
  return { row: added, state: worst, requirements, underived: false };
}

/** Every added activity, worst first, so the thing holding a start up is the first row. */
export function spaceReadiness(
  rows: readonly AddedActivityRow[],
  activities: SpaceActivities,
  connections: readonly ConnectionRow[],
): ActivityReadiness[] {
  return rows
    .map((row) => readinessOf(row, activities, connections))
    .sort((left, right) => rank(left.state) - rank(right.state));
}

/**
 * Whether this activity may start. Only this list may hold Start back, and a manifest that
 * could not be read leaves it enabled: a read failure must never become a stopped show.
 */
export function startable(readiness: ActivityReadiness): boolean {
  if (readiness.underived) return true;
  return !readiness.requirements.some((one) => blocks(one.state));
}

/** Triggers this added activity declares with no enabled `EventBinding` targeting them. */
export function unfedTriggers(
  added: AddedActivityRow,
  activities: SpaceActivities,
  wires: readonly EventBinding[],
): NamedTrigger[] {
  const entry = activities.entryFor(added);
  if (entry === undefined) return [];

  const fed = new Set(
    wires
      .filter(
        (one) => one.enabled !== false && one.target_added_activity_id === added.added_activity_id,
      )
      .map((one) => one.trigger),
  );

  return triggersOf(entry).filter((trigger) => !fed.has(trigger.name));
}

export interface UnfedTriggers {
  row: AddedActivityRow;
  triggers: NamedTrigger[];
}

/** Every added activity with at least one unfed trigger, paired with those triggers. */
export function spaceUnfedTriggers(
  rows: readonly AddedActivityRow[],
  activities: SpaceActivities,
  wires: readonly EventBinding[],
): UnfedTriggers[] {
  return rows
    .map((row) => ({ row, triggers: unfedTriggers(row, activities, wires) }))
    .filter((one) => one.triggers.length > 0);
}

/** True when this activity declares at least one trigger and every one of them is unfed. */
export function nothingDrivesIt(
  added: AddedActivityRow,
  activities: SpaceActivities,
  wires: readonly EventBinding[],
): boolean {
  const entry = activities.entryFor(added);
  if (entry === undefined) return false;
  const declared = triggersOf(entry);
  if (declared.length === 0) return false;
  return unfedTriggers(added, activities, wires).length === declared.length;
}

/** Canvases this added activity declares that must be paired and hold no live pairing. */
export function unpairedCanvases(
  added: AddedActivityRow,
  activities: SpaceActivities,
  pairings: readonly CanvasPairing[],
): NamedCanvas[] {
  const entry = activities.entryFor(added);
  if (entry === undefined) return [];

  const held = new Set(
    pairings.filter((one) => one.revoked_at === null).map((one) => one.canvas_kind),
  );
  return canvasesOf(entry).filter(
    (canvas) => canvas.required === true && canvas.attach === "paired" && !held.has(canvas.kind),
  );
}

/** True once this activity declares a required paired canvas nothing is paired to. */
export function needsPairedCanvas(
  added: AddedActivityRow,
  activities: SpaceActivities,
  pairings: readonly CanvasPairing[],
): boolean {
  return unpairedCanvases(added, activities, pairings).length > 0;
}

export interface VersionPending {
  readonly running: string;
  readonly configured: string;
}

/** The live mount's version against the configured one, when they differ. */
export function versionPending(added: AddedActivityRow): VersionPending | undefined {
  const mount = added.mount;
  if (mount === null || !isLive(runStatusOf(mount))) return undefined;
  const running = mount.activity_version;
  if (running == null || running === "" || running === added.activity_version) return undefined;
  return { running, configured: added.activity_version };
}

export interface PresentedChip {
  readonly label: string;
  readonly tone: StatusTone;
}

/**
 * The one vocabulary a customer sees for a requirement's state, wherever it is shown: readiness
 * itself, an interface's wiring reading, or a plain bound-or-not position, all collapse here.
 */
const PRESENTED: Record<ReadinessState, PresentedChip> = {
  ready: { label: "Ready", tone: "success" },
  unwired: { label: "Needs wiring", tone: "warning" },
  ambiguous: { label: "Ambiguous", tone: "warning" },
  unavailable: { label: "Unavailable", tone: "danger" },
  optional_unwired: { label: "Optional", tone: "neutral" },
};

/** The chip a `ReadinessState` reads as. Every screen showing a requirement's state uses this. */
export function presentedChip(state: ReadinessState): PresentedChip {
  return PRESENTED[state];
}

/** The Needs panel's header chip for one activity: `presentedChip`, with three additions on top. */
export function activityChip(
  readiness: ActivityReadiness,
  undriven: boolean,
  unpaired = false,
  versionMismatch = false,
): PresentedChip {
  const tone = presentedChip(readiness.state).tone;
  if (readiness.underived) return { label: "not derived", tone };
  if (readiness.state === "ready" && unpaired) {
    return { label: "Needs a paired canvas", tone: "warning" };
  }
  if (readiness.state === "ready" && versionMismatch) {
    return { label: "Restart to apply", tone: "warning" };
  }
  if (readiness.state === "ready" && undriven) return { label: "Nothing drives it", tone: "warning" };
  return { label: readiness.state, tone };
}
